import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { Loader2, RefreshCw, Search, Trash2, UserPlus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Pagination } from '../components/ui/pagination';
import { EmptyState } from '../components/ui/empty-state';
import { TableSkeleton } from '../components/ui/skeleton';
import { useToast } from '../components/ui/toast';
import {
  getPendingCandidateList,
  deletePendingCandidate,
  convertPendingCandidate,
  convertBrowserCandidate,
  scanResumeMailbox,
  PendingCandidate,
} from '../api/pendingCandidate';
import {
  getJDListForSelect,
  getUserList,
  getCandidateChannels,
  PRESET_CHANNELS,
  JDForSelect,
  UserInfo,
} from '../api/candidate';

const CUSTOM_CHANNEL_VALUE = '__custom__';
const SCREENING_COLORS = {
  '适合': 'bg-emerald-50 text-emerald-700',
  '不适合': 'bg-rose-50 text-rose-700',
  '待人工确认': 'bg-amber-50 text-amber-700',
};
const CHECK_LABELS = { met: '符合', not_met: '不符合', unknown: '信息不足' };

const ScreeningStatus: React.FC<{ item: PendingCandidate }> = ({ item }) => {
  if (!item.browser_source_id) return <span className="text-slate-400">-</span>;
  const result = item.screening_result;
  if (!result) return <span className="text-slate-400">未初筛</span>;
  return (
    <div className="space-y-1">
      <span className={`inline-flex px-2 py-0.5 rounded text-xs ${item.screening_stale ? 'bg-slate-100 text-slate-500' : SCREENING_COLORS[result.recommendation]}`}>
        {item.screening_stale ? '结果已过期' : result.recommendation}
      </span>
      <div className="text-xs text-slate-500">{item.screening_stale ? '资料或岗位已变化，请重新初筛' : 'AI 建议 · 待 HR 复核'}</div>
      <div className="text-xs text-slate-500">{result.screening_mode === 'hard_conditions' ? `硬条件筛选 · 已选 ${result.selected_conditions?.length || result.checks.length} 项` : result.screening_mode === 'free' ? 'DeepSeek 自由筛选（宽松）' : '历史初筛'}</div>
    </div>
  );
};

const getErrorDetail = (reason: unknown): string => {
  const error = reason as {
    response?: { data?: { detail?: unknown } };
    message?: string;
  };
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg);
        }
        return JSON.stringify(item);
      })
      .join('；');
  }
  return error?.message || '转候选人失败';
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 人才储备 - 待处理 tab
 *
 * 展示邮箱附件和 BOSS 待处理资料；附件经 HR 确认后进入正式招聘流程。
 */
export const PendingCandidatePanel: React.FC<{ isHR: boolean }> = ({ isHR }) => {
  const { showToast } = useToast();

  const [keyword, setKeyword] = useSessionState('pendingCandidateKeyword', '');
  const [previewItem, setPreviewItem] = useState<PendingCandidate | null>(null);
  const [page, setPage] = useSessionState('pendingCandidatePage', 1);
  const [pageSize, setPageSize] = useSessionState('pendingCandidatePageSize', 20);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState<PendingCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const listRequest = useRef(0);

  // 勾选状态（与候选人管理页面一致）
  const [selectedRows, setSelectedRows] = useState<PendingCandidate[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [batchDeleteDialog, setBatchDeleteDialog] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);

  // 下拉数据
  const [jdList, setJdList] = useState<JDForSelect[]>([]);
  const [userList, setUserList] = useState<UserInfo[]>([]);
  const [channelList, setChannelList] = useState<string[]>(PRESET_CHANNELS);

  // 新增候选人弹窗
  const [convertDialog, setConvertDialog] = useState<{ open: boolean; item: PendingCandidate | null }>({
    open: false,
    item: null,
  });
  const [convertForm, setConvertForm] = useState({ jd_id: '', screening_owner_id: '', channel: '' });
  const [customChannel, setCustomChannel] = useState('');
  const [converting, setConverting] = useState(false);
  const convertTargets = convertDialog.item ? [convertDialog.item] : selectedRows;
  const hasBrowserTargets = convertTargets.some((item) => Boolean(item.browser_source_id));
  const onlyBrowserTargets = convertTargets.length > 0 && convertTargets.every((item) => Boolean(item.browser_source_id));

  // 删除确认弹窗
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: PendingCandidate | null }>({
    open: false,
    item: null,
  });
  const [deleting, setDeleting] = useState(false);

  const loadList = useCallback(async () => {
    const request = ++listRequest.current;
    setLoading(true);
    try {
      const res = await getPendingCandidateList({ keyword, page, page_size: pageSize });
      if (request !== listRequest.current) return;
      setItems(res.items || []);
      setTotal(res.total || 0);
      setHasLoaded(true);
      setSelectedRows([]);
    } catch (error) {
      if (request !== listRequest.current) return;
      console.error('加载待处理候选人失败:', error);
      showToast('加载待处理候选人失败', 'error');
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, page, pageSize]);

  useEffect(() => {
    loadList();
    return () => { listRequest.current += 1; };
  }, [loadList]);

  useEffect(() => {
    if (!hasLoaded) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [hasLoaded, total, page, pageSize, setPage]);

  // 更新全选状态
  useEffect(() => {
    setSelectAll(items.length > 0 && selectedRows.length === items.length);
  }, [selectedRows, items]);

  const handleSelectRow = (item: PendingCandidate, checked: boolean) => {
    setSelectedRows((prev) => (checked ? [...prev, item] : prev.filter((r) => r.id !== item.id)));
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setSelectedRows(checked ? [...items] : []);
  };

  useEffect(() => {
    (async () => {
      try {
        const [jds, users, channels] = await Promise.all([
          getJDListForSelect(),
          getUserList(),
          getCandidateChannels(),
        ]);
        setJdList(jds.items || []);
        setUserList(users || []);
        if (channels.length > 0) setChannelList(channels);
      } catch (error) {
        console.error('加载下拉数据失败:', error);
      }
    })();
  }, []);

  // 手动扫描邮箱
  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await scanResumeMailbox();
      showToast(res?.message || '扫描完成');
      loadList();
    } catch (error: any) {
      console.error('扫描失败:', error);
      showToast(error?.response?.data?.detail || '扫描失败', 'error');
    } finally {
      setScanning(false);
    }
  };

  const openConvertDialog = (item: PendingCandidate | null) => {
    const targets = item ? [item] : selectedRows;
    if (targets.some((target) => target.browser_source_id && !target.file_path)) {
      showToast('所选资料包含仅有在线正文的 BOSS 简历，请先保存附件简历后再新增候选人', 'warning');
      return;
    }
    if (!isHR && targets.some((target) => target.browser_source_id)) {
      showToast('BOSS 附件需要由 HR 确认新增候选人', 'warning');
      return;
    }
    const allBrowser = targets.length > 0 && targets.every((target) => target.browser_source_id);
    setConvertForm({
      jd_id: item?.browser_source_id && item.jd_id ? String(item.jd_id) : '',
      screening_owner_id: '',
      channel: allBrowser ? 'Boss直聘' : '',
    });
    setCustomChannel('');
    setConvertDialog({ open: true, item });
  };

  // 批量新增候选人：对选中的每份简历使用同一套JD/负责人/渠道
  const handleBatchConvert = () => {
    if (selectedRows.length === 0) {
      showToast('请选择要新增为候选人的简历', 'warning');
      return;
    }
    openConvertDialog(null);
  };

  const handleBatchDelete = () => {
    if (selectedRows.length === 0) {
      showToast('请选择要删除的简历', 'warning');
      return;
    }
    setBatchDeleteDialog(true);
  };

  const handleBatchDeleteConfirm = async () => {
    setBatchDeleting(true);
    try {
      const results = await Promise.allSettled(
        selectedRows.map((row) => deletePendingCandidate(row.id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        showToast(`成功删除 ${results.length - failed} 份，${failed} 份删除失败`, 'warning');
      } else {
        showToast(`成功删除 ${results.length} 份待处理简历`);
      }
      setBatchDeleteDialog(false);
      loadList();
    } catch (error) {
      console.error('批量删除失败:', error);
      showToast('批量删除失败', 'error');
    } finally {
      setBatchDeleting(false);
    }
  };

  const handleConvertConfirm = async () => {
    const targets = convertDialog.item ? [convertDialog.item] : selectedRows;
    if (targets.length === 0) return;
    if (targets.some((item) => item.browser_source_id && (!item.file_path || !isHR))) {
      showToast('BOSS 资料需取得附件简历后，由 HR 确认新增', 'warning');
      return;
    }
    if (!convertForm.jd_id) {
      showToast('请选择JD', 'warning');
      return;
    }
    if (!convertForm.screening_owner_id) {
      showToast('请选择简历筛选负责人', 'warning');
      return;
    }
    const finalChannel =
      convertForm.channel === CUSTOM_CHANNEL_VALUE ? customChannel.trim() : convertForm.channel;
    if (!onlyBrowserTargets && !finalChannel) {
      showToast('请选择渠道', 'warning');
      return;
    }

    setConverting(true);
    try {
      const results = await Promise.allSettled(
        targets.map((item) => {
          const selection = {
            jd_id: Number(convertForm.jd_id),
            screening_owner_id: Number(convertForm.screening_owner_id),
          };
          return item.browser_source_id
            ? convertBrowserCandidate(item.browser_source_id, selection)
            : convertPendingCandidate(item.id, { ...selection, channel: finalChannel });
        })
      );
      const failures = results
        .map((result, index) =>
          result.status === 'rejected'
            ? `${targets[index].name}：${getErrorDetail(result.reason)}`
            : null
        )
        .filter((item): item is string => Boolean(item));
      const successCount = results.length - failures.length;

      if (failures.length > 0) {
        const detail = failures.slice(0, 2).join('；');
        const suffix = failures.length > 2 ? ` 等 ${failures.length} 个失败` : '';
        showToast(`成功新增 ${successCount} 个候选人，${detail}${suffix}`, successCount > 0 ? 'warning' : 'error');
      } else {
        showToast(`成功添加 ${results.length} 个候选人`);
      }
      const convertedIds = new Set(
        results
          .map((result, index) => (result.status === 'fulfilled' ? targets[index].id : null))
          .filter((id): id is number => id !== null)
      );
      if (convertedIds.size > 0) {
        setItems((prev) => prev.filter((item) => !convertedIds.has(item.id)));
        setSelectedRows((prev) => prev.filter((item) => !convertedIds.has(item.id)));
        setTotal((prev) => Math.max(prev - convertedIds.size, 0));
      }
      if (failures.length === 0) {
        setConvertDialog({ open: false, item: null });
      }
      loadList();
    } catch (error: any) {
      console.error('转候选人失败:', error);
      showToast(error?.response?.data?.detail || '转候选人失败', 'error');
    } finally {
      setConverting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.item) return;
    setDeleting(true);
    try {
      await deletePendingCandidate(deleteDialog.item.id);
      showToast('删除成功');
      setDeleteDialog({ open: false, item: null });
      loadList();
    } catch (error: any) {
      console.error('删除失败:', error);
      showToast(error?.response?.data?.detail || '删除失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* 筛选与操作 */}
      <Card className="flex-shrink-0">
        <CardContent className="py-3">
          <div className="flex items-center gap-3">
            <div className="relative w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
                placeholder="搜索简历名称"
                className="pl-9 h-8 text-sm text-gray-600"
              />
            </div>
            <span className="text-sm text-slate-500">共 {total} 份待处理简历</span>
          </div>
        </CardContent>
      </Card>

      {/* 操作按钮区（位于筛选卡片与表格卡片之间） */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 text-sm border border-slate-300 text-slate-600 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleBatchDelete}
            disabled={selectedRows.length === 0}
          >
            <Trash2 className="w-3.5 h-3.5" />
            批量删除
          </button>
          <button
            className="px-3 py-1.5 text-sm border border-blue-400 text-blue-600 rounded-md hover:bg-blue-50 transition-colors flex items-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleBatchConvert}
            disabled={selectedRows.length === 0}
          >
            <UserPlus className="w-3.5 h-3.5" />
            批量新增候选人
          </button>
        </div>
        <div>
          {isHR && (
            <button
              className="px-3 py-1.5 text-sm border border-slate-300 text-slate-600 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-1 whitespace-nowrap disabled:opacity-50"
              onClick={handleScan}
              disabled={scanning}
            >
              {scanning ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              立即扫描邮箱
            </button>
          )}
        </div>
      </div>

      {/* 待处理列表 */}
      <Card className="flex-1 flex flex-col min-h-0">
        <CardContent className="pt-4 pb-4 flex-1 flex flex-col min-h-0">
          {loading ? (
            <TableSkeleton rows={6} cols={7} />
          ) : (
            <>
              <div className="relative overflow-auto flex-1 min-h-0">
                <Table className="min-w-[1390px]">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[50px] min-w-[50px] sticky left-0 bg-gray-50 z-20 align-middle">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                        </div>
                      </TableHead>
                      <TableHead className="w-[280px] min-w-[280px] sticky left-[50px] bg-gray-50 z-20 whitespace-nowrap border-r">简历名称</TableHead>
                      <TableHead className="w-[130px] min-w-[130px] whitespace-nowrap">采集人</TableHead>
                      <TableHead className="w-[170px] min-w-[170px] whitespace-nowrap">上传时间</TableHead>
                      <TableHead className="w-[340px] min-w-[340px] whitespace-nowrap">来源 / 联系方式</TableHead>
                      <TableHead className="w-[180px] min-w-[180px] whitespace-nowrap">在线简历初筛</TableHead>
                      <TableHead className="w-[200px] min-w-[200px] sticky right-0 bg-gray-50 z-20 whitespace-nowrap border-l">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-0">
                          <EmptyState
                            title="暂无待处理简历"
                            description="可通过简历邮箱抓取或 BOSS 插件保存待处理资料，确认后再新增候选人"
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="py-2 sticky left-0 bg-white z-10 align-middle">
                            <div className="flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={selectedRows.some((r) => r.id === item.id)}
                                onChange={(e) => handleSelectRow(item, e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="py-2 sticky left-[50px] bg-white z-10 border-r whitespace-nowrap">
                            <button
                              className="text-blue-600 hover:text-blue-700 hover:underline text-left"
                              onClick={() => item.browser_source_id ? setPreviewItem(item) : window.open(`/${item.file_path}`, '_blank')}
                            >
                              {item.attachment_name || item.name || '-'}
                            </button>
                            {item.browser_source_id && <div className="text-xs text-slate-400 mt-1">{item.name} · {item.file_path ? '已保存附件' : '仅在线正文，待获取附件'}</div>}
                          </TableCell>
                          <TableCell className="py-2 whitespace-nowrap text-slate-600">
                            {item.fetched_by_name || '-'}
                          </TableCell>
                          <TableCell className="py-2 whitespace-nowrap text-slate-600">
                            {formatDateTime(item.created_at)}
                          </TableCell>
                          <TableCell className="py-2">
                            <div className="text-slate-700 truncate max-w-[320px]" title={item.browser_source_id ? item.boss_job_title || item.boss_job_key || undefined : item.email_subject}>
                              {item.browser_source_id ? `Boss直聘 · ${item.boss_job_title || '岗位待绑定'}` : item.email_subject || '-'}
                            </div>
                            <div className="text-xs text-slate-400 truncate max-w-[320px]" title={item.email_from}>
                              {item.browser_source_id ? [item.phone, item.email].filter(Boolean).join(' / ') || '暂无联系方式' : item.email_from || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <ScreeningStatus item={item} />
                          </TableCell>
                          <TableCell className="py-2 sticky right-0 bg-white z-10 border-l">
                            <div className="flex items-center gap-1 whitespace-nowrap">
                              <button
                                className="px-2 py-1 text-sm text-blue-600 rounded hover:bg-blue-50 transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                disabled={Boolean(item.browser_source_id && (!item.file_path || !isHR))}
                                title={item.browser_source_id ? (!isHR ? '需要由 HR 确认新增' : !item.file_path ? '请先取得附件简历' : undefined) : undefined}
                                onClick={() => openConvertDialog(item)}
                              >
                                <UserPlus className="w-3.5 h-3.5" />
                                新增候选人
                              </button>
                              <button
                                className="px-2 py-1 text-sm text-red-600 rounded hover:bg-red-50 transition-colors flex items-center gap-1"
                                onClick={() => setDeleteDialog({ open: true, item })}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                删除
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {total > 0 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={page}
                    pageSize={pageSize}
                    total={total}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 新增候选人弹窗 */}
      <Dialog
        open={convertDialog.open}
        onOpenChange={(open) => setConvertDialog({ open, item: open ? convertDialog.item : null })}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>新增候选人</DialogTitle>
            <DialogDescription>
              {convertDialog.item
                ? `将「${convertDialog.item.name}」转入正式招聘流程，选择JD、简历筛选负责人和渠道`
                : `将选中的 ${selectedRows.length} 份简历转入正式招聘流程，统一使用下面的JD、简历筛选负责人和渠道`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {hasBrowserTargets && (
              <p className="rounded-md bg-blue-50 p-3 text-sm text-blue-700">
                确认后将解析附件简历、评分并创建正式招聘待办。BOSS 来源渠道固定为 Boss直聘。
              </p>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">选择JD <span className="text-red-500">*</span></label>
              <SearchableSelect
                options={jdList.map((jd) => ({ value: String(jd.id), label: `${jd.id} - ${jd.job_title}` }))}
                value={convertForm.jd_id}
                onValueChange={(value) => setConvertForm((prev) => ({ ...prev, jd_id: value }))}
                placeholder="请选择JD"
                searchPlaceholder="搜索JD..."
                emptyText="无匹配JD"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">选择简历筛选负责人 <span className="text-red-500">*</span></label>
              <SearchableSelect
                options={userList.map((u) => ({
                  value: String(u.id),
                  label: `${u.id}-${u.real_name || u.username}${u.department ? `（${u.department}）` : ''}`,
                }))}
                value={convertForm.screening_owner_id}
                onValueChange={(value) => setConvertForm((prev) => ({ ...prev, screening_owner_id: value }))}
                placeholder="请选择负责人"
                searchPlaceholder="搜索ID、姓名或部门"
                emptyText="无匹配负责人"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                渠道 <span className="text-red-500">*</span>
              </label>
              {onlyBrowserTargets ? <p className="text-sm text-slate-600">Boss直聘</p> : <Select
                value={convertForm.channel}
                onValueChange={(value) => {
                  setConvertForm((prev) => ({ ...prev, channel: value }));
                  if (value !== CUSTOM_CHANNEL_VALUE) setCustomChannel('');
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="请选择渠道" />
                </SelectTrigger>
                <SelectContent>
                  {channelList.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                  <SelectItem value={CUSTOM_CHANNEL_VALUE}>自定义</SelectItem>
                </SelectContent>
              </Select>}
              {!onlyBrowserTargets && convertForm.channel === CUSTOM_CHANNEL_VALUE && (
                <Input
                  value={customChannel}
                  onChange={(e) => setCustomChannel(e.target.value)}
                  placeholder="请输入自定义渠道名称"
                  className="h-9 text-sm"
                  maxLength={100}
                />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialog({ open: false, item: null })}>
              取消
            </Button>
            <Button onClick={handleConvertConfirm} disabled={converting} className="bg-blue-600 hover:bg-blue-700">
              {converting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              确认新增
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOSS 资料预览独立于转正式弹窗，未选择新增时也能直接查看。 */}
      <Dialog open={Boolean(previewItem)} onOpenChange={(open) => !open && setPreviewItem(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewItem?.name} · BOSS 简历</DialogTitle>
            <DialogDescription>在线简历用于初筛；取得附件简历后，可选择岗位和负责人转入正式招聘流程。</DialogDescription>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-5 min-w-0">
              <dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-2 text-sm break-words">
                <dt className="text-slate-500">BOSS 岗位</dt><dd>{previewItem.boss_job_title || '未绑定岗位'}{previewItem.boss_job_key && <span className="text-slate-400">（{previewItem.boss_job_key}）</span>}</dd>
                <dt className="text-slate-500">系统 JD</dt><dd>{previewItem.jd_id ? `${previewItem.jd_id} · ${jdList.find((jd) => jd.id === previewItem.jd_id)?.job_title || '已绑定岗位'}` : '尚未绑定，请在插件中绑定后初筛'}</dd>
                <dt className="text-slate-500">电话</dt><dd>{previewItem.phone || '尚未获取'}</dd>
                <dt className="text-slate-500">邮箱</dt><dd>{previewItem.email || '尚未获取'}</dd>
                <dt className="text-slate-500">来源</dt><dd>{previewItem.source_url ? <a className="text-blue-600 hover:underline" href={previewItem.source_url} target="_blank" rel="noreferrer">查看 BOSS 来源页面</a> : 'Boss直聘'}</dd>
                <dt className="text-slate-500">附件</dt><dd>{previewItem.file_path ? <a className="text-blue-600 hover:underline" href={`/${previewItem.file_path}`} target="_blank" rel="noreferrer">{previewItem.attachment_name || '查看附件简历'}</a> : '尚未保存附件，仅在线正文不能转正式候选人'}</dd>
              </dl>
              <div className="rounded-lg border border-slate-200 p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-medium text-slate-700">AI 在线简历初筛</h3>
                  <ScreeningStatus item={previewItem} />
                </div>
                {previewItem.screening_result ? <>
                  {previewItem.screening_stale && <p className="text-xs text-amber-700">以下为历史结果，仅供参考。请在插件中重新初筛。</p>}
                  <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">{previewItem.screening_result.summary}</p>
                  {(['reasons', 'strengths', 'follow_up'] as const).map(field => {
                    const entries = previewItem.screening_result?.[field];
                    return entries?.length ? <div key={field} className="rounded bg-slate-50 p-3 text-sm space-y-1">
                      <div className="font-medium">{{ reasons: '判断理由', strengths: '匹配亮点', follow_up: '需沟通核实' }[field]}</div>
                      {entries.map((text, index) => <p key={index} className="text-slate-600 whitespace-pre-wrap break-words">{text}</p>)}
                    </div> : null;
                  })}
                  <div className="space-y-2">
                    {previewItem.screening_result.checks.map((check, index) => (
                      <div key={index} className="rounded bg-slate-50 p-3 text-sm space-y-1">
                        <div className="font-medium text-slate-700 break-words">{check.kind === 'hard' ? '硬性条件' : '软性条件'} · {check.requirement} <span className="text-slate-500">— {CHECK_LABELS[check.status]}</span></div>
                        <p className="text-slate-500 whitespace-pre-wrap break-words">{check.evidence || '暂无可核对证据'}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400">初筛时间：{formatDateTime(previewItem.screening_result.evaluated_at)} · 初筛结论供 HR 复核，不会自动通知或拒绝候选人</p>
                </> : <p className="text-sm text-slate-500">尚未进行 AI 初筛，可在插件中绑定岗位后单独启动。</p>}
              </div>
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-slate-700">在线简历快照</h3>
                <pre className="max-h-[35vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-4 text-sm font-sans text-slate-600">{previewItem.online_resume || '未采集在线简历正文'}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 批量删除确认弹窗 */}
      <Dialog open={batchDeleteDialog} onOpenChange={setBatchDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>批量删除</DialogTitle>
            <DialogDescription>
              确定要删除选中的 {selectedRows.length} 份待处理简历吗？删除后暂存的附件文件也会一并移除。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchDeleteDialog(false)}>
              取消
            </Button>
            <Button variant="destructive" onClick={handleBatchDeleteConfirm} disabled={batchDeleting}>
              {batchDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              确定删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, item: open ? deleteDialog.item : null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
            <DialogDescription>
              确定要删除待处理简历「{deleteDialog.item?.name}」吗？删除后暂存的附件文件也会一并移除。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>
              取消
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              确定删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PendingCandidatePanel;
